/**
 * Pure functions for facility form data.
 * Single responsibility: parse facility to form shape, normalize input values.
 */

const EMPTY_FORM = {
  name: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
};

/**
 * Parse facility entity to form data (handles flat, address object, or address string).
 * @param {Object|null} facility - Facility from API (may have address object, flat fields, or address string)
 * @returns {Object} Form data shape { name, street, city, state, zipCode }
 */
export function parseFacilityToFormData(facility) {
  if (!facility || typeof facility !== 'object') return { ...EMPTY_FORM };

  const name = facility.name ?? '';

  const hasFlatFields =
    facility.street != null ||
    facility.city != null ||
    facility.state != null ||
    facility.zipCode != null;
  if (hasFlatFields) {
    return {
      name,
      street: facility.street ?? '',
      city: facility.city ?? '',
      state: facility.state ?? '',
      zipCode: facility.zipCode ?? '',
    };
  }

  const addr = facility.address;
  if (addr && typeof addr === 'object' && !Array.isArray(addr)) {
    return {
      name,
      street: addr.street ?? addr.street_address ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      zipCode: addr.zipCode ?? addr.zip_code ?? '',
    };
  }

  if (addr && typeof addr === 'string') {
    const parts = String(addr)
      .split(',')
      .map((p) => p.trim());
    const street = parts[0] || '';
    const city = parts[1] || '';
    const stateZip = parts[2] || '';
    const stateZipParts = stateZip.split(/\s+/).filter(Boolean);
    const state = stateZipParts[0] || '';
    const zipCode = stateZipParts[1] || '';
    return { name, street, city, state, zipCode };
  }

  return { name, ...EMPTY_FORM };
}

/**
 * Normalize raw input value by field (state uppercase, zipCode digits only).
 * @param {string} field - Field name
 * @param {string} value - Raw value
 * @returns {string}
 */
export function normalizeInputValue(field, value) {
  if (field === 'state') {
    return value.toUpperCase().slice(0, 2);
  }
  if (field === 'zipCode') {
    const cleaned = value.replace(/[^\d-]/g, '');
    return cleaned.length > 10 ? cleaned.slice(0, 10) : cleaned;
  }
  return value;
}

export function getEmptyFormData() {
  return { ...EMPTY_FORM };
}
